import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Search, Ban, CheckCircle } from 'lucide-react';
import { facilityService } from '../../services/facility-service';
import type { FacilityAdminDto, FacilityAdminQueryDto } from '../../types/admin-api';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';

const columnHelper = createColumnHelper<FacilityAdminDto>();

export function FacilityList() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('all');
  const [type, setType] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    facilityId: string;
    facilityName: string;
    action: 'enable' | 'disable';
  }>({
    isOpen: false,
    facilityId: '',
    facilityName: '',
    action: 'enable',
  });
  const [_reason, setReason] = useState('');

  const query: FacilityAdminQueryDto = {
    page,
    pageSize,
    search: search || undefined,
    status: status === 'all' ? undefined : (status as 'active' | 'disabled'),
    type: type || undefined,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['facilities', query],
    queryFn: () => facilityService.getAll(query),
  });

  const setStatusMutation = useMutation({
    mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
      facilityService.setStatus(id, { isEnabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facilities'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setConfirmDialog({ isOpen: false, facilityId: '', facilityName: '', action: 'enable' });
      setReason('');
    },
  });

  const handleStatusChange = (facility: FacilityAdminDto, action: 'enable' | 'disable') => {
    setConfirmDialog({
      isOpen: true,
      facilityId: facility.id,
      facilityName: facility.name,
      action,
    });
  };

  const handleConfirmStatusChange = () => {
    const isEnabled = confirmDialog.action === 'enable';
    setStatusMutation.mutate({
      id: confirmDialog.facilityId,
      isEnabled,
    });
  };

  const columns = [
    columnHelper.accessor('name', {
      header: 'Facility Name',
      cell: (info) => (
        <div>
          <div className="font-medium text-ink-primary">{info.getValue()}</div>
          {info.row.original.businessName && (
            <div className="text-sm text-ink-muted">{info.row.original.businessName}</div>
          )}
        </div>
      ),
    }),
    columnHelper.accessor('type', {
      header: 'Type',
      cell: (info) => <span className="text-ink-secondary">{info.getValue()}</span>,
    }),
    columnHelper.accessor('city', {
      header: 'City',
      cell: (info) => <span className="text-ink-secondary">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('capacity', {
      header: 'Capacity',
      cell: (info) => <span className="text-ink-secondary">{info.getValue()}</span>,
    }),
    columnHelper.accessor('pricePerHour', {
      header: 'Price/Hour',
      cell: (info) => (
        <span className="text-ink-secondary">
          PLN {info.getValue().toLocaleString('pl-PL', { minimumFractionDigits: 2 })}
        </span>
      ),
    }),
    columnHelper.accessor('isEnabled', {
      header: 'Status',
      cell: (info) => <StatusBadge isEnabled={info.getValue()} />,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <div className="flex items-center gap-2">
          {info.row.original.isEnabled ? (
            <button
              onClick={() => handleStatusChange(info.row.original, 'disable')}
              className="p-1 text-brand-warm hover:bg-brand-warm/10 rounded"
              title="Disable"
            >
              <Ban size={18} />
            </button>
          ) : (
            <button
              onClick={() => handleStatusChange(info.row.original, 'enable')}
              className="p-1 text-brand-confirm hover:bg-brand-confirm/10 rounded"
              title="Enable"
            >
              <CheckCircle size={18} />
            </button>
          )}
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: data?.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (error) {
    return (
      <div className="bg-brand-warm/20 border border-brand-warm/40 text-brand-warm px-4 py-3 rounded">
        Error loading facilities: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Filters */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
        <h2 className="font-display text-xl font-semibold tracking-tight text-ink-primary mb-4">All Facilities</h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-subtle"
              size={20}
            />
            <input
              type="text"
              placeholder="Search by name..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-surface-input border border-white/15 text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 bg-surface-input border border-white/15 text-ink-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active Only</option>
            <option value="disabled">Disabled Only</option>
          </select>

          {/* Type Filter */}
          <input
            type="text"
            placeholder="Filter by type"
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 bg-surface-input border border-white/15 text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />

          {/* Clear Filters */}
          {(search || status !== 'all' || type) && (
            <button
              onClick={() => {
                setSearch('');
                setStatus('all');
                setType('');
                setPage(1);
              }}
              className="px-4 py-2 text-white bg-white/10 border border-white/30 rounded-lg hover:bg-white/20"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-ink-muted">Loading facilities...</div>
        ) : data?.data && data.data.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-6 py-3 text-left text-xs font-medium text-ink-secondary uppercase tracking-wider"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-white/10">
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="hover:bg-white/5">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-sm">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.pagination && data.pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between">
                <div className="text-sm text-ink-secondary">
                  Showing page {data.pagination.page} of {data.pagination.totalPages} (
                  {data.pagination.totalItems} total)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= data.pagination.totalPages}
                    className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-ink-muted">No facilities found</div>
        )}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => {
          setConfirmDialog({ isOpen: false, facilityId: '', facilityName: '', action: 'enable' });
          setReason('');
        }}
        onConfirm={handleConfirmStatusChange}
        title={confirmDialog.action === 'enable' ? 'Enable Facility' : 'Disable Facility'}
        message={`Are you sure you want to ${confirmDialog.action} "${confirmDialog.facilityName}"?`}
        confirmText={confirmDialog.action === 'enable' ? 'Enable' : 'Disable'}
        confirmVariant={confirmDialog.action === 'disable' ? 'danger' : 'primary'}
        isLoading={setStatusMutation.isPending}
      />
    </div>
  );
}
